import { ApiResponse, BfiApi, IApiResponse } from './bfiApi';
import { ISearchCriteria, IBfiNumberCheck } from './common';
import { IListItemResponse } from './listApi';
import {
    FloodReportStatusType,
    LoanType,
    LoanStatusType,
    LoanGridType,
    RentalLeaseType,
    LostLoanType,
    LoanGradeType,
} from '../../../constants/src';
import FileDownload from 'js-file-download';
import { IAddress } from 'libs/store/src/cross';
import { UnderwritingPaidType } from '../../../../apps/admin/src/loans/common/constants/UnderwritingPaidType';

export interface ILoanSearchCriteria extends ISearchCriteria {
    gridType: LoanGridType;
}

export interface IStandardLoansSearchCriteria extends ISearchCriteria {
    statusId: LoanStatusType;
    loanTypeId: LoanType;
}

export interface IRentalLoansSearchCriteria extends ISearchCriteria {
    term: number;
}

export interface ILoansSearchCriteria extends ISearchCriteria {
    statusId?: LoanStatusType;
    loanTypeId?: LoanType;
}

export interface ILOCLoansSearchCriteria extends ISearchCriteria {
    statusId: LoanStatusType;
}

export interface IBuildRentConversion {
    conversionDate: Date;
    conversionNotes: string;
}

export interface IRentalLease {
    id: number;
    leaseTypeId: RentalLeaseType;
    monthlyRent: number;
    startDate: Date;
    endDate: Date;
    notes: string;
}

export interface IRentalUnit {
    id: number;
    unitName: string;
    leases: [IRentalLease];
}

export interface IRentalDscr extends ILoanQuickSummary {
    units: [IRentalUnit];
}

export interface ILoanProperty extends IAddress {
    id?: number;
    legalDescription: string;
    unitCount: number;
    structureTypeId: number;
    applicationPropertyId?: number;
    appraisedDate: Date;
    statusId: number;
    landCost: number;
    verticalCost: number;
    threeMilePopulation: number;
    threeMileFiveYearGrowth: number;
    floodReport: IFloodReport;
    currentDebt?: number;
    isLoanProceedsUsedPropertyImprovements?: boolean;
    moreThan10kExpenditures?: boolean;
    unitsIncomeRestricted?: number;
    isProblemProperty: boolean;
    propertyDevelopmentTypeId?: number;
    propertyDevelopmentTypeOther?: string;
    yearBuilt?: number;
    ownedThroughDevelopment?: boolean;
    acquisitionDate?: string;
    buildTypeId?: number;
    certificateOfOccupancyTypeId?: number;
    workNeeded?: boolean;
    workNeededDescription?: string;
    expectedListingPrice?: number;
    hoaDues?: boolean;
    hadPermitAtClosingTypeId?: number;
    preferredInterestPaymentTypeId?: number;
}

export interface ILoanFile {
    id: number;
    fileTypeId: number;
    fileTypeName: string;
    url: string;
    tempFileName: string;
}

export interface IFloodReportContact {
    name: string;
    email: string;
    phone: string;
    extension: string;
    faxNumber: string;
    infoRequired: string;
    comments: string;
}

export interface IFloodReport {
    id: number;
    url: string;
    statusId: FloodReportStatusType;
    resultDescription: string;
    contact: IFloodReportContact;
}

export interface ILoanBorrower {
    id: number;
    companyName: string;
    personName: string;
    ownerId: string;
    ownerName: string;
}

export interface IRecycleBin {
    id?: number;
    loanTypeId: number;
    statusId: number;
    bfiNumber: string;
    loanNumber?: number;
    borrowerEntity: string;
    broker: string;
    city: string;
    state: string;
    commitment?: number;
    promissoryNote?: number;
    underwriter: string;
    owner: string;
    approvalDate?: Date;
}

export interface ILoanExpense {
    id: number;
    name: string;
    amount: number;
    isSystem: boolean;
}

export interface ILoanBase {
    id: number;
    bfiNumber: string;
    loanNumber?: number;
    pointOfContactId: number;
    underwriterId: number;
    callCodeType?: number;
    loanTypeId: LoanType;
    statusId: LoanStatusType;
    commitment?: number;
    promissoryNote?: number;
    bridgeFunded?: number;
    closingDate?: Date;
    approvalDate?: Date;
    borrowerEntityId?: number;
    brokerAgreementId?: number;
    isMasterNote: boolean;
    interestRate?: number;
    reserveRate?: number;
    interestAccrualTypeId?: number;
    interestRateFloor?: number;
    interestRateCeiling?: number;
    underwritingFee?: number;
    originationFee?: number;
    brokerShare?: number;
    brokerAdditional?: number;
    expenses: [ILoanExpense];
    //Rental Fields
    amortizationTypeId?: number;
    purposeTypeId?: number;
    isAutoPay: boolean;
    ever30DaysDelinquent: boolean;
}

export interface IStandardLoan extends ILoanBase {
    underwriteId?: number;
    properties: [ILoanProperty];
    applicationId?: number;
    inSpecialFloodHazardArea: boolean;
    masterNoteId: number;
    masterNoteBfiNumber: string;
}

export interface INewSubNote {
    masterNoteId: number;
    masterNoteBfiNumber: string;
    interestRate: number;
    reserveRate: number;
    maturityDate: Date;
    pointOfContactId: string;
    underwriterId: string;
    inSpecialFloodHazardArea: false;
    loanTypeId: LoanType;
    statusId: LoanStatusType;
    approvalDate?: Date;
    borrowerEntityId: number;
    brokerId: number;
    entireStructure?: boolean;
    expenses: [ILoanExpense];
}

export interface IMasterNote extends ILoanBase {
    noteDate?: Date;
    maturityDate?: Date;
    brokerFeeTypeId?: number;
    brokerFeeName: string;
    brokerFootnote: string;
    interestTypeId: number;
    sbaGuarantyPercent: number;
    sbaUpfrontFee: number;
    borrowerCharge: number;
    recurringSbaFeePercent: number;
    interestPaymentDueTypeId: number;
    loanGradeType?: LoanGradeType;
    brokerFee?: number;
    dueDate1: string;
    dueDate2: string;
    dueDate3: string;
    dueDate4: string;
}

export interface IBorrowersListItem {
    id: number;
    borrowerEntity: string;
    borrowerName: string;
    email: string;
    ownerName: string;
    ownerEmail: string;
}

export interface IBrokersListItem {
    id: number;
    companyName: string;
    personName: string;
}

export interface IMasterNoteGridData {
    id: number;
    borrower: string;
    city: string;
    state: string;
    bfiNumber: string;
    loanNumber: string;
    creditLine: number;
    interestRate: number;
    underwriter: string;
    owner: string;
    approvalDate: Date;
    daysSinceApproval: number;
    broker: string;
    subNoteCount: number;
    lostReason: string;
    commitment: number;
    promissoryNote: number;
}

export interface ISubNoteGridData extends IMasterNoteGridData {
    loanToValue: number;
}

export interface IChangesHistoryGridData {
    id: number;
    dateAndTime: Date;
    userName: string;
    reason: string;
    notes: string;
}

export interface IChangeEntry {
    ChangeType: string;
    SectionName: string;
    FieldName: string;
    FromValue: string;
    ToValue: string;
}

export interface IAuditHistoryDetail {
    id: number;
    dateAndTime: Date;
    userName: string;
    reason: string;
    notes: string;
    entries: [IChangeEntry];
}

export interface IApplBorrower {
    BorrowerEntity: string;
    borrowerName: string;
    phone: string;
    email: string;
    address: IAddress;
}

export interface IBaseLoanGridData {
    id: number;
    statusId: LoanStatusType;
    loanTypeId: LoanType;
    borrowerEntity: string;
    broker: string;
    borrowerCity: string;
    borrowerState: string;
    propertyCity: string;
    propertyState: string;
    bfiNumber: string;
    loanNumber: string;
    commitment: number;
    promissoryNote: number;
    underwriter: string;
    owner: string;
    approvalDate: Date;
    daysSinceApproval: number;
    interestRate: number;
    lostReason: string;
    lostType: string;
    appraisedValue: number;
    borrower: IApplBorrower;
    address: IAddress;
    loanToValue: number;
    canReleaseHoldback: boolean;
    canDscr: boolean;
    canConvertRental: boolean;
    isRental: boolean;
    canRollback: boolean;
    canRepay: boolean;
}

export interface ICreditScore {
    id: number;
    name: string;
    score1?: number;
    score2?: number;
}

export interface IBorrower {
    id: number;
    borrowerEntity: string;
    firstName: string;
    middleName: string;
    lastName: string;
    suffix: string;
    phone: string;
    email: string;
    street1: string;
    street2: string;
    city: string;
    state: string;
    zipCode: string;
    tin?: string;
    ownerId?: number;
    ownerName: string;
    netWorth?: number;
    adjustedNetWorth?: number;
    creditScores: [ICreditScore];
}

export interface IBorrowerValidate {
    id: number;
    borrowerEntity: string;
    contactFirstName: string;
    contactMiddleName: string;
    contactLastName: string;
    contactSuffix: string;
}

export interface ICheckLoanAndBfiNumber {
    isValid: boolean;
    bfiNumberValid: boolean;
    loanNumberValid: boolean;
    message: string;
}

export interface IMasterNoteValidationResult extends ICheckLoanAndBfiNumber {
    oneMasterNotePerEntityValid: boolean;
}

export interface ILoanRestore {
    id: number;
    statusId: number;
    statusName: string;
}

export interface ILostCriteria {
    lostTypeId: number;
    reason: string;
    isManual: boolean;
    noticeDate?: Date | null | undefined;
    noticeTempFileName: string;
}

export interface ILoanCloseReq {
    noteDate: Date;
    maturityDate: Date;
    buildRentConversionDeadline?: Date;
}

export interface ILoanCloseResp {
    loanTypeId: LoanType;
    noteDate?: Date;
    maturityDate?: Date;
}

export interface ILoanInfo {
    id: number;
    extensionDate: Date;
    isContractual: boolean;
    feePaid: number;
    appraisalUpdate: boolean;
    appraisedDate?: Date;
    appraisedValue?: number;
}

export interface IRepayment {
    id: number;
    payDate: Date;
    amount: number;
    notes: string;
    isFullPayment: boolean;
    exitByTypeId?: number;
    RefiLenderName?: string;
    salePrice?: number;
    remainingPrincipal: number;
    propertyName: string;
}

export interface ILoanRepayment {
    repaymentType: number;
}

export interface IRepaymentSummary {
    propertyId: number;
    payDate: Date;
    amount: number;
    isFullPayment: boolean;
}

export interface IRentalHoldbackRelease {
    id: number;
    releaseDate: Date;
    amount: number;
    notes: string;
    isFullRelease: boolean;
}

export interface ILoanExtension {
    id: number;
    extensionDate: Date;
    isContractual: boolean;
    feePaid: number;
    appraisalUpdated: boolean;
    appraisedValue?: number;
    appraisedDate?: Date;
}

export interface ILoanQuickSummary {
    id: number;
    loanTypeId: LoanType;
    lostLoanType?: LostLoanType;
    statusId: LoanStatusType;
    statusComment: string;
    isPortfolio: boolean;
    bfiNumber: string;
    loanNumber?: number;
    brokerName: string;
    brokerContact: string;
    borrowerEntity: string;
    borrowerContact: string;
    underwriter: string;
    noteDate?: Date;
    maturityDate?: Date;
    address: IAddress;
    appraisedDate?: Date;
    hasMultipleAppraisedDates: boolean;
    allAppraisedDatesSameMonthAndYear: boolean;
    allAppraisedDatesSameMonthAndYearDisplay?: Date;
    appraisedValue?: number;
    ltv: number;
    extensions: [ILoanExtension];
    commitment?: number;
    withdrawalReasons: [string];
}

export interface ILoanExtensionsGrid {
    isEditable: boolean;
    extensions: [ILoanExtension];
}

export interface IRepaymentGrid {
    repayments: [IRepayment];
    isEditable: boolean;
}

export interface IRepayLoan extends ILoanQuickSummary {
    LoanLevelPaymentAllowed: boolean;
    propertyCommitment: number;
    paidToDate: number;
    repayments: [IRepayment];
    hasAnyForcePlacedInsurance: boolean;
}

export interface IRentalHoldbackReleaseModal extends ILoanQuickSummary {
    holdback: number;
    releasedToDate: number;
    releases: [IRentalHoldbackRelease];
}

export interface IRentalHoldbackReleaseGrid {
    isEditable: boolean;
    releases: [IRentalHoldbackRelease];
}

export interface ILoanTotals {
    count: number;
    prommissoryNote: number;
    committed: number;
    funded: number;
}

export interface IChangeLoanType {
    id: number;
    loanTypeId: number;
    masterNoteId?: number | null;
}

export interface ISubNoteSummary {
    id: number;
    bfiNumber: string;
    loanTypeId: LoanType;
    statusId: LoanStatusType;
    closingDate?: Date;
    maturityDate?: Date;
    commitment: number;
}

export interface ITerminateMasterNote {
    id: number;
    terminationDate: Date;
    notes: string;
}

export interface ITerminateMasterNote extends ILoanQuickSummary {
    subNotes: [ISubNoteSummary];
}

export interface IMasterNoteValidation extends IBfiNumberCheck {
    borrowerEntityId: number;
}

export interface ILoanSetupSheet {
    uwScore: number;
    uwBankruptcyPlusScore: number;
    floatingRateForInterestRate: boolean;
    interestRateAtStart: number;
    suppressCeiling: boolean;
    hasCeiling: boolean;
    floatingRateForCeiling: boolean;
    interestRateCeiling: number;
    suppressFloor: boolean;
    hasFloor: boolean;
    floatingRateForFloor: boolean;
    interestRateFloor: number;
    netWorthAmount?: number;
    adjustedNetWorthAmount?: number;
    underwritingFeePaid: UnderwritingPaidType;
    estimatedInterestAndFees: number;
    estimatedClosingCosts: number;
    owners?: ILoanSetupSheetOwners[];
}
export interface ILoanSetupSheetOwners {
    principalId: number;
    entityName: string;
    name: string;
    percentOwnership: number;
    creditScore?: number;
}

export interface ILoanSetupSheetResponse extends Omit<IApiResponse, 'result'> {
    result: ILoanSetupSheetOwners[];
}

export interface IBorrowersListItemResponse extends Omit<IApiResponse, 'result'> {
    result: [IBorrowersListItem];
}

export interface IBrokersListItemResponse extends Omit<IApiResponse, 'result'> {
    result: [IBrokersListItem];
}

export interface ILoanResponse extends Omit<IApiResponse, 'result'> {
    result: IStandardLoan;
}

export interface INewSubNoteResponse extends Omit<IApiResponse, 'result'> {
    result: INewSubNote;
}

export interface IRecycleBinResponse extends Omit<IApiResponse, 'result'> {
    result: IRecycleBin;
}

export interface IMasterNoteResponse extends Omit<IApiResponse, 'result'> {
    result: IMasterNote;
}

export interface ICheckLoanAndBfiNumberResponse extends Omit<IApiResponse, 'result'> {
    result: ICheckLoanAndBfiNumber;
}

export interface IMasterNotesGridResponse extends Omit<IApiResponse, 'result'> {
    result: {
        totalCount: number;
        results: [IMasterNoteGridData];
        summary: [number];
    };
}

export interface ISubNotesGridResponse extends Omit<IApiResponse, 'result'> {
    result: {
        totalCount: number;
        results: [ISubNoteGridData];
        summary: [number];
    };
}

export interface IStandardLoansGridResponse extends Omit<IApiResponse, 'result'> {
    result: {
        totalCount: number;
        results: [IBaseLoanGridData];
    };
}

export interface ILoanRestoreResponse extends Omit<IApiResponse, 'result'> {
    result: ILoanRestore;
}

export interface IFloodReportResponse extends Omit<IApiResponse, 'result'> {
    result: IFloodReport;
}

export interface IBorrowerConversionResponse extends Omit<IApiResponse, 'result'> {
    result: number;
}

export interface ILoanCloseResponse extends Omit<IApiResponse, 'result'> {
    result: ILoanCloseResp;
}

export interface ILoanSummary extends Omit<IApiResponse, 'result'> {
    result: [ILoanSummary];
}

export interface ILoanExtensionsGridResponse extends Omit<IApiResponse, 'result'> {
    result: ILoanExtensionsGrid;
}

export interface IRepayLoanResponse extends Omit<IApiResponse, 'result'> {
    result: IRepayLoan;
}

export interface IRepaymentGridResponse extends Omit<IApiResponse, 'result'> {
    result: IRepaymentGrid;
}

export interface ILoanRepaymentsResponse extends Omit<IApiResponse, 'result'> {
    result: [IRepaymentSummary];
}

export interface IAuditHistoryDetailResponse extends Omit<IApiResponse, 'result'> {
    result: IAuditHistoryDetail;
}

export interface IReleaseHoldbackModalResponse extends Omit<IApiResponse, 'result'> {
    result: IRentalHoldbackReleaseModal;
}

export interface IReleaseHoldbackGridResponse extends Omit<IApiResponse, 'result'> {
    result: [IRentalHoldbackReleaseGrid];
}

export interface IRentalDscrResponse extends Omit<IApiResponse, 'result'> {
    result: IRentalDscr;
}

export interface IStandardLoanSummaryResponse extends Omit<IApiResponse, 'result'> {
    result: ILoanTotals;
}

export interface ITerminateMasterNoteResponse extends Omit<IApiResponse, 'result'> {
    result: IRepayLoan;
}

export interface ILoanSummaryDashboardExportRequest {
    closedByMonthDisplayType: string;
    closedByPOCDisplayType: string;
    closedSelectedMonth: string | null;
    outstandingByMonthDisplayType: string;
    outstandingByPOCDisplayType: string;
}

export interface ILoanSummaryDashboardRequest {
    statusTypeId: number | undefined;
}

//Outstanding Dashboard
export interface ILoanSummaryDashboardDataResponse extends Omit<IApiResponse, 'result'> {
    result: ILoanSummaryDashboardData;
}

export interface ILoanSummaryDashboardData {
    closedData: ILoanSummaryDashboardClosedData;
    outstandingData: ILoanSummaryDashboardOutstandingData;
}
////////Closed//////////////////
export interface ILoanSummaryDashboardClosedData {
    loanTypeSummary: ILoanSummaryDashboardClosedLoanTypeSummary[];
    pocPivotSummary: ILoanSummaryDashboardClosedPOCPivotSummary[];
}

//Closed Summary

export interface ILoanSummaryDashboardClosedLoanTypeSummary {
    loanTypeId: number;
    allTotals: ILoanSummaryDashboardClosedTotals;
    t12Totals: ILoanSummaryDashboardClosedTotals;
    committedAmount: number;
    fundedAmount: number;
    number: number;
    monthToDate: ILoanSummaryDashboardClosedDetailCounts;
    monthlyDetails: ILoanSummaryDashboardClosedLoanTypeMonthlyDetails[];
}

export interface ILoanSummaryDashboardClosedTotals {
    committedAmount: number;
    fundedAmount: number;
    number: number;
}

export interface ILoanSummaryDashboardClosedLoanTypeMonthlyDetails {
    month: string;
    monthFullName: string;
    counts: ILoanSummaryDashboardClosedDetailCounts;
    isT12: boolean;
}

export interface ILoanSummaryDashboardClosedDetailCounts {
    committedAmount: number;
    fundedAmount: number;
    number: number;
}

//Closed Poc Pivot

export interface ILoanSummaryDashboardClosedPOCPivotSummary {
    loanTypeId: number;
    pocColumns: ILoanSummaryDashboardClosedPOCAggregate[];
}

export interface ILoanSummaryDashboardClosedPOCAggregate {
    pointOfContact: string;
    pointOfContactName: string;
    pointOfContactFullName: string;
    groupId?: number | undefined;
    committed: number;
    funded: number;
    count: number;
    monthlyDetails: ILoanSummaryDashboardClosedPOCMonthlyDetails[];
}

export interface ILoanSummaryDashboardClosedPOCMonthlyDetails {
    month: string;
    committed: number;
    funded: number;
    count: number;
}

////////Outstanding/////////////
export interface ILoanSummaryDashboardOutstandingData {
    loanTypeSummary: ILoanSummaryDashboardOutstandingLoanTypeSummary[];
    pocPivotSummary: ILoanSummaryDashboardOutstandingPOCPivotSummary[];
}

//Outstanding Summary
export interface ILoanSummaryDashboardOutstandingLoanTypeSummary {
    loanTypeId: number;
    committedAmount: number;
    fundedAmount: number;
    number: number;
    monthToDate: ILoanSummaryDashboardOutstandingDetailCounts;
    monthlyDetails: ILoanSummaryDashboardOutstandingLoanTypeMonthlyDetails[];
    older: ILoanSummaryDashboardOutstandingDetailCounts;
}

export interface ILoanSummaryDashboardOutstandingLoanTypeMonthlyDetails {
    month: string;
    monthFullName: string;
    counts: ILoanSummaryDashboardOutstandingDetailCounts;
    isT12: boolean;
}

export interface ILoanSummaryDashboardOutstandingDetailCounts {
    committedAmount: number;
    fundedAmount: number;
    number: number;
}

//Outstanding Poc Pivot
export interface ILoanSummaryDashboardOutstandingPOCPivotSummary {
    loanTypeId: number;
    pocColumns: ILoanSummaryDashboardOutstandingPOCAggregate[];
}

export interface ILoanSummaryDashboardOutstandingPOCAggregate {
    pointOfContact: string;
    pointOfContactName: string;
    pointOfContactFullName: string;
    groupId?: number | undefined;
    committed: number;
    funded: number;
    count: number;
    monthlyDetails: ILoanSummaryDashboardOutstandingPOCMonthlyDetails[];
}

export interface ILoanSummaryDashboardOutstandingPOCMonthlyDetails {
    month: string;
    committed: number;
    funded: number;
    count: number;
}

export class LoanApi extends BfiApi {
    updateStandardLoan(loan: IStandardLoan): Promise<IApiResponse> {
        switch (loan.loanTypeId) {
            case LoanType.Rental:
                return super.post('/loans/rental/update', loan);
            case LoanType.BuildRent:
                return super.post('/loans/buildrent/update', loan);
            case LoanType.SubNoteConstruction:
            case LoanType.SubNoteFixFlip:
                return super.post('/loans/subnote/update', loan);
            default:
                return super.post('/loans/standard/update', loan);
        }
    }

    masterNoteValidate(model: IMasterNoteValidation): Promise<IMasterNoteValidationResult> {
        return super.post('/loans/masternotes/validate', model);
    }

    masterNoteSave(loan: IMasterNote): Promise<IApiResponse> {
        return super.post('/loans/masternotes/update', loan);
    }

    masterNoteGetById(loanId): Promise<IMasterNoteResponse> {
        return super.get(`/loans/masternotes/${loanId}`);
    }

    getGoogleFonts(): Promise<IApiResponse> {
        return super.get('/list/getGoogleFonts');
    }

    searchBfiNumbers(loanTypeId: LoanType, searchText: string, pageNumber: number, pageSize: number): Promise<IListItemResponse> {
        return super.get(
            `/loans/search/bfinumber?loanTypeId=${loanTypeId}&searchText=${searchText ?? ''}&pageNumber=${pageNumber ?? 1}&pageSize=${pageSize ?? 50}`
        );
    }

    searchSubNoteBfiNumbers(searchText: string, pageNumber: number, pageSize: number): Promise<IListItemResponse> {
        return super.get(
            `/loans/subnotes/search/bfinumbers?searchText=${searchText ?? ''}&pageNumber=${pageNumber ?? 1}&pageSize=${pageSize ?? 50}`
        );
    }

    getStandardLoanByBfiNumber(bfiNumber): Promise<ILoanResponse> {
        return super.get(`/loans/standard/bfinumber/${bfiNumber}`);
    }

    checkLoanAndBfiNumbers(model: IBfiNumberCheck): Promise<ICheckLoanAndBfiNumberResponse> {
        return super.post('/loans/checkbfinumbers', model);
    }

    checkBrokerLoanAndBfiNumbers(model: IBfiNumberCheck): Promise<ICheckLoanAndBfiNumberResponse> {
        return super.post('/loans/brokers/checkbfinumbers', model);
    }

    getStandardLoanById(loanId): Promise<ILoanResponse> {
        return super.get(`/loans/standard/${loanId}`);
    }

    getGrid_MasterNotes(criteria: ILOCLoansSearchCriteria): Promise<IMasterNotesGridResponse> {
        return super.post('/loans/grids/masternotes', criteria);
    }

    getGrid_SubNotes(criteria: ILOCLoansSearchCriteria): Promise<ISubNotesGridResponse> {
        return super.post('/loans/grids/subnotes', criteria);
    }

    getGrid_StandardLoans(criteria: IStandardLoansSearchCriteria): Promise<IBaseLoanGridData> {
        return super.post('/loans/grids/standard', criteria);
    }

    getGrid_RentalLoans(criteria: IRentalLoansSearchCriteria): Promise<IBaseLoanGridData> {
        return super.post('/loans/grids/standard/rentals', criteria);
    }

    getGrid_StandardLoans_Summary(loanTypeId: LoanType, statusId: LoanStatusType): Promise<IStandardLoanSummaryResponse> {
        return super.get(`/loans/grids/standard/summary?loantypeid=${loanTypeId ?? ''}&statusid=${statusId ?? ''}`);
    }

    getGrid_LOCLoans_Summary(statusId: LoanStatusType): Promise<IStandardLoanSummaryResponse> {
        return super.get(`/loans/grids/loc/summary?statusid=${statusId ?? ''}`);
    }

    getGrid_PortfolioLoans(loanId: number, criteria: IStandardLoansSearchCriteria): Promise<IBaseLoanGridData> {
        return super.post(`/loans/grids/portfolio/${loanId}`, criteria);
    }

    getGrid_Search(criteria: ILoansSearchCriteria): Promise<IBaseLoanGridData> {
        return super.post('/loans/grids/search', criteria);
    }

    createSubnote(masterNoteId: number): Promise<INewSubNoteResponse> {
        return super.get(`/loans/masternotes/${masterNoteId}/subnote/new`);
    }

    createStandardLoan(loanTypeId?: LoanType, bClientId?: number): Promise<ILoanResponse> {
        return super.get(`/loans/standard/new?loanTypeId=${loanTypeId ?? ''}&bclientid=${bClientId ?? ''}`);
    }

    setLoanDeleted(loanId: number, reason: string): Promise<IApiResponse> {
        return super.post(`/loans/${loanId}/delete`, { reason });
    }

    setLoanLost(loanId: number, payload: ILostCriteria): Promise<ILoanCloseResponse> {
        return super.post(`/loans/${loanId}/lost`, payload);
    }

    getRecycleBin(criteria: ISearchCriteria): Promise<IRecycleBinResponse> {
        return super.post('/loans/grids/recyclebin', criteria);
    }

    restoreLoan(loanId: number): Promise<ILoanRestoreResponse> {
        return super.post(`/loans/${loanId}/restore`);
    }

    orderFloodReport(propertyId: number): Promise<IApiResponse> {
        return super.get(`/loans/properties/${propertyId}/floodreport`);
    }

    cancelFloodReport(propertyId: number): Promise<IFloodReportResponse> {
        return super.get(`/loans/properties/${propertyId}/floodreport/cancel`);
    }

    getLoanExtension(loanId: number): Promise<ILoanSummary> {
        return super.get(`/loans/${loanId}/extensions`);
    }

    getLoanExtensionsGrid(loanId: number): Promise<ILoanExtensionsGridResponse> {
        return super.get(`/loans/${loanId}/extensions/grid`);
    }

    setLoanExtension(loanId: number, extension: ILoanExtension): Promise<IApiResponse> {
        return super.post(`/loans/${loanId}/extensions`, extension);
    }

    getPropertyRepayment(propertyId: number): Promise<IRepayLoanResponse> {
        return super.get(`/loans/properties/${propertyId}/repayments`);
    }

    getRepaymentsGrid(propertyId: number): Promise<IRepaymentGridResponse> {
        return super.get(`/loans/properties/${propertyId}/repayments/grid`);
    }

    getLoanRepaymentsPortfolioOverages(loanId: number): Promise<IRepaymentGridResponse> {
        return super.get(`/loans/portfolio/${loanId}/overages/repayments/grid`);
    }

    getRepaymentsSummaryForLoan(loanId: number): Promise<ILoanRepaymentsResponse> {
        return super.get(`/loans/${loanId}/repayments/summary`);
    }

    getAuditHistory(loanId: number): Promise<IApiResponse> {
        return super.get(`loans/${loanId}/auditing`);
    }

    getAuditHistoryDetail(changeId: number): Promise<IAuditHistoryDetailResponse> {
        return super.get(`loans/auditing/${changeId}`);
    }

    setPropertyRepayment(propertyId: number, payment: IRepayment): Promise<IApiResponse> {
        return super.post(`/loans/properties/${propertyId}/repayments`, payment);
    }

    getLoanRepayment(loanId: number): Promise<IRepayLoanResponse> {
        return super.get(`/loans/${loanId}/repayments`);
    }

    setLoanRepayment(loanId: number, payment: IRepayment): Promise<IApiResponse> {
        return super.post(`/loans/${loanId}/repayments`, payment);
    }

    getRentalHoldbackReleases(propertyId: number): Promise<IReleaseHoldbackModalResponse> {
        return super.get(`/loans/properties/${propertyId}/releaseholdbacks`);
    }

    setRentalHoldbackRelease(propertyId: number, release: IRentalHoldbackReleaseModal): Promise<IApiResponse> {
        return super.post(`/loans/properties/${propertyId}/releaseholdbacks`, release);
    }

    getRentalHoldbackReleaseGrid(propertyId: number): Promise<IReleaseHoldbackGridResponse> {
        return super.get(`/loans/properties/${propertyId}/releaseholdbacks/grid`);
    }

    getRentalDscr(propertyId: number): Promise<IRentalDscrResponse> {
        return super.get(`/loans/properties/${propertyId}/dscr`);
    }

    setRentalDscr(propertyId: number, dscr: IRentalDscr): Promise<IApiResponse> {
        return super.post(`/loans/properties/${propertyId}/dscr`, dscr);
    }

    getBuildRentConvert(loanId: number): Promise<IRentalDscrResponse> {
        return super.get(`/loans/${loanId}/converttorental`);
    }

    setBuildRentConvert(loanId: number, conversion: IBuildRentConversion): Promise<IApiResponse> {
        return super.post(`/loans/${loanId}/converttorental`, conversion);
    }

    getRevertToApproved(loanId: number): Promise<ILoanSummary> {
        return super.get(`/loans/${loanId}/revert/approved`);
    }

    setRevertToApproved(loanId: number): Promise<IApiResponse> {
        return super.post(`/loans/${loanId}/revert/approved`);
    }

    getLoanSummary(loanId: number): Promise<ILoanSummary> {
        return super.get(`/loans/${loanId}/summary`);
    }

    getChangeLoanType(loanId: number): Promise<IApiResponse> {
        return super.get(`/loans/${loanId}/changeloantype`);
    }

    setChangeLoanType(loanId: number, model: IChangeLoanType): Promise<IApiResponse> {
        return super.post(`/loans/${loanId}/changeloantype`, model);
    }

    linkLoanToBClient(loanId: number, bClientId: number): Promise<IApiResponse> {
        return super.post(`/loans/${loanId}/linktobclient/${bClientId}`);
    }

    getGrid_linkLoanToBClient(criteria: ISearchCriteria): Promise<IBaseLoanGridData> {
        return super.post(`/loans/grids/linktobclient/search`, criteria);
    }

    getGrid_EntityLoans(
        entityId: number,
        masterNotes: boolean,
        criteria: ISearchCriteria,
        isClosed?: boolean
    ): Promise<IBaseLoanGridData> {
        if (isClosed != null) {
            return super.post(
                `/loans/grids/entities/${entityId ?? 0}?masternotes=${masterNotes ?? false}&isClosed=${isClosed ?? false}`,
                criteria
            );
        } else {
            return super.post(`/loans/grids/entities/${entityId ?? 0}?masternotes=${masterNotes ?? false}`, criteria);
        }
    }

    exportLoans(loanTypeId: number, statusId: LoanStatusType) {
        this.axiosInstance({
            url: `/loans/export?loanTypeId=${loanTypeId ?? ''}&statusId=${statusId ?? ''}`,
            method: 'GET',
            responseType: 'blob', // Important
        }).then((response) => {
            FileDownload(response.data, 'export.csv');
        });
    }

    exportRentalLoans(statusId: LoanStatusType) {
        this.axiosInstance({
            url: `/loans/export/rentals?statusId=${statusId ?? ''}`,
            method: 'GET',
            responseType: 'blob', // Important
        }).then((response) => {
            FileDownload(response.data, 'export.csv');
        });
    }

    exportSubNotes(statusId: LoanStatusType) {
        this.axiosInstance({
            url: `/loans/export/subnotes?statusId=${statusId ?? ''}`,
            method: 'GET',
            responseType: 'blob', // Important
        }).then((response) => {
            FileDownload(response.data, 'export.csv');
        });
    }

    exportMasterNotes(statusId: LoanStatusType) {
        this.axiosInstance({
            url: `/loans/export/masternotes?statusId=${statusId ?? ''}`,
            method: 'GET',
            responseType: 'blob', // Important
        }).then((response) => {
            FileDownload(response.data, 'export.csv');
        });
    }

    getMasterNoteTerminate(loanId: number): Promise<ITerminateMasterNoteResponse> {
        return super.get(`/loans/masternotes/${loanId}/terminate`);
    }

    setMasterNoteTerminate(model: ITerminateMasterNote): Promise<IApiResponse> {
        return super.post(`/loans/masternotes/terminate`, model);
    }

    generateLoanSetupSheet(loanId: number, model: ILoanSetupSheet) {
        return this.axiosInstance({
            url: `/loansetupsheets/generate/${loanId}`,
            method: 'POST',
            data: model,
            responseType: 'blob',
        });
    }

    getApplicableLoanSetupSheetOwners(loanId: number): Promise<ILoanSetupSheetResponse> {
        return super.get(`/loansetupsheets/applicableOwners/${loanId}`);
    }

    getLoanSummaryDashboardData(criteria: ILoanSummaryDashboardRequest): Promise<ILoanSummaryDashboardDataResponse> {
        return super.post(`/loans/dashboards/loanSummaryData`, criteria);
    }

    exportLoanSummaryDashboardData(criteria: ILoanSummaryDashboardExportRequest): Promise<IApiResponse> {
        return super.post('/loans/dashboards/loanSummaryData/export', criteria, {
            responseType: 'blob',
        });
    }
}
